import { ActivityHistoryItem, ActivityHistoryList, DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  useGetChecklistTemplateQuery,
  useGetChecklistTemplatesQuery,
  useGetOrCreateChecklistTemplateDraftMutation,
  usePublishChecklistTemplateDraftMutation,
} from '@/services/checklistTemplates';
import { isErrorWithData } from '@/services/helpers';
import {
  PalmettoButton,
  PalmettoIcon,
  PalmettoModal,
  PalmettoTag,
  PalmettoTextarea,
} from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ChecklistTemplateDocument,
  ChecklistTemplateSection,
  ChecklistTemplateStatus,
  Clientize,
  ProgramTypeLabels,
  QueueTypeLabel,
} from 'types';

import { ChecklistItemCard } from './ChecklistItemCard';
import { TemplateItemFormValue } from './ChecklistTemplateItemsEditor';

// The API computes `draftId` on the response (the sibling open draft's own id, when one exists
// for this template's group) — it is not a stored field on ChecklistTemplateDocument itself.
type TemplateRow = Clientize<ChecklistTemplateDocument> & {
  createdByName?: string;
  createdByEmail?: string;
  draftId?: string;
};

const emptyValueLabel = '--';
const pageSize = 20;

export const ChecklistTemplateDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: templateData, isLoading } = useGetChecklistTemplateQuery(id!);
  const template = templateData as TemplateRow | undefined;
  const [getOrCreateDraft, { isLoading: isCreatingDraft }] = useGetOrCreateChecklistTemplateDraftMutation();
  const [publishDraft, { isLoading: isPublishing }] = usePublishChecklistTemplateDraftMutation();

  const [page, setPage] = useState(0);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  // A raw useState-driven value re-rendered inside PalmettoModal on every keystroke causes
  // Ark UI's FocusTrap to reactivate and reset focus — react-hook-form's Controller isolates
  // re-renders to just the changed field, so the modal itself never re-renders while typing.
  const {
    control: publishModalControl,
    handleSubmit: handlePublishModalSubmit,
    reset: resetPublishModalForm,
  } = useForm<{ draftNotes: string }>({ defaultValues: { draftNotes: '' } });

  const { data: historicalResponse, isFetching: isHistoryFetching } = useGetChecklistTemplatesQuery(
    {
      isCurrent: false,
      templateGroupId: template?.templateGroupId,
      status: ChecklistTemplateStatus.archived,
      limit: pageSize,
      offset: page * pageSize,
    },
    { skip: !template },
  );

  const handleDuplicate = useCallback(() => {
    navigate('/admin/checklists/new', { state: { duplicateFrom: template } });
  }, [navigate, template]);

  const handleEditDraft = useCallback(async () => {
    if (!id) return;
    try {
      const draft = await getOrCreateDraft(id).unwrap();
      navigate(`/admin/checklists/${draft.id}/edit-draft`);
    } catch (error) {
      console.error('Error opening draft for editing:', error);
      if (isErrorWithData(error)) {
        toast.error(error.data.message);
      } else {
        toast.error('Error opening draft for editing.');
      }
    }
  }, [getOrCreateDraft, id, navigate]);

  const handleOpenPublishModal = useCallback(() => {
    resetPublishModalForm({ draftNotes: template?.draftNotes ?? '' });
    setIsPublishModalOpen(true);
  }, [resetPublishModalForm, template]);

  const handleClosePublishModal = useCallback(() => {
    setIsPublishModalOpen(false);
  }, []);

  const submitPublishModal = useCallback(
    async (data: { draftNotes: string }) => {
      if (!template) return;
      try {
        await publishDraft({ id: template.id, draftNotes: data.draftNotes }).unwrap();
        toast.success('Template published');
        setIsPublishModalOpen(false);
      } catch (error) {
        console.error('Error publishing draft:', error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Error publishing draft.');
        }
      }
    },
    [publishDraft, template],
  );

  const handlePublishModalConfirm = useCallback(() => {
    void handlePublishModalSubmit(submitPublishModal)();
  }, [handlePublishModalSubmit, submitPublishModal]);

  const handlePreviousPage = useCallback(() => {
    setPage((prev) => prev - 1);
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const sortedItems = useMemo(
    () => (template ? [...template.items].toSorted((a, b) => a.order - b.order) : []),
    [template],
  );

  const sortedSections = useMemo(
    () => (template?.sections ?? []).slice().sort((a, b) => a.order - b.order),
    [template],
  );

  const sectionGroups = useMemo(() => {
    if (sortedSections.length === 0) {
      return [{ section: null as ChecklistTemplateSection | null, items: sortedItems }];
    }
    const sectionMap = new Map<string, ChecklistTemplateSection>(sortedSections.map((s) => [s.key, s]));
    const grouped = new Map<string | null, TemplateItemFormValue[]>();
    for (const item of sortedItems) {
      const key = item.sectionKey && sectionMap.has(item.sectionKey) ? item.sectionKey : null;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(item as TemplateItemFormValue);
    }
    const result: { section: ChecklistTemplateSection | null; items: TemplateItemFormValue[] }[] = [];
    const ungrouped = grouped.get(null);
    if (ungrouped && ungrouped.length > 0) result.push({ section: null, items: ungrouped });
    for (const section of sortedSections) {
      const items = grouped.get(section.key) ?? [];
      if (items.length > 0) result.push({ section, items });
    }
    return result;
  }, [sortedSections, sortedItems]);

  // The active version always redirects to its sibling draft (when one exists) so the read
  // view shows in-progress edits rather than stale published content.
  const isRedirectingToDraft = Boolean(
    template && template.status !== ChecklistTemplateStatus.draft && template.draftId,
  );

  useEffect(() => {
    if (template && template.status !== ChecklistTemplateStatus.draft && template.draftId) {
      navigate(`/admin/checklists/${template.draftId}`, { replace: true });
    }
  }, [template, navigate]);

  if (isLoading || isRedirectingToDraft) {
    return (
      <DsmPageContainer title="Loading...">
        <div>Loading...</div>
      </DsmPageContainer>
    );
  }

  if (!template) {
    return (
      <DsmPageContainer title="Not Found">
        <div>Template not found.</div>
      </DsmPageContainer>
    );
  }

  const historicalVersions = historicalResponse?.data ?? [];
  const totalHistorical = historicalResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalHistorical / pageSize));

  const pageVersions = historicalVersions;

  const queueTypeDisplay = template.queueType
    ? QueueTypeLabel[template.queueType as keyof typeof QueueTypeLabel] || template.queueType
    : emptyValueLabel;

  const programTypesDisplay = template.programTypes?.length
    ? template.programTypes.map((pt) => ProgramTypeLabels[pt as keyof typeof ProgramTypeLabels] || pt).join(', ')
    : 'Global';

  const isFirstPage = page === 0;
  const isLastPage = page >= totalPages - 1;

  return (
    <DsmPageContainer title={template.name}>
      <PageHeader
        title={template.name}
        eyebrow={
          <Link
            to="/admin/checklists"
            className="inline-flex items-center gap-2 text-text-primary no-underline hover:underline"
          >
            <PalmettoIcon name="chevronLeft" size="sm" />
            Back to List
          </Link>
        }
        description={
          template.status === ChecklistTemplateStatus.draft ? (
            <PalmettoTag label="Draft" color="grey" size="sm" className="w-fit" />
          ) : undefined
        }
      >
        <div className="flex flex-nowrap gap-2">
          <PalmettoButton emphasis="secondary" onClick={handleDuplicate}>
            Duplicate
          </PalmettoButton>
          <PalmettoButton
            emphasis="primary"
            onClick={handleEditDraft}
            leadingIcon={<PalmettoIcon name="penEdit" size="sm" />}
            disabled={isCreatingDraft}
          >
            {isCreatingDraft ? 'Opening...' : 'Edit'}
          </PalmettoButton>
          {template.status === ChecklistTemplateStatus.draft && (
            <PalmettoButton emphasis="primary" onClick={handleOpenPublishModal} disabled={isPublishing}>
              Publish
            </PalmettoButton>
          )}
        </div>
      </PageHeader>

      {/* Metadata Section */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="gap-1 flex flex-col">
            <div className="text-sm text-text-secondary">Queue Type</div>
            <div className="font-bold">{queueTypeDisplay}</div>
          </div>
          <div className="gap-1 flex flex-col">
            <div className="text-sm text-text-secondary">Program Type</div>
            <div className="font-bold">{programTypesDisplay}</div>
          </div>
        </div>
        <div className="gap-1 flex flex-col">
          <div className="text-sm text-text-secondary">Description</div>
          <div>{template.description}</div>
        </div>
      </div>

      {/* Checklist Items Section */}
      <div className="mb-6 flex flex-col gap-4">
        <h2 className="text-left heading-200 text-text-primary">Checklist Items</h2>
        <div className="flex flex-col gap-16">
          {sectionGroups.map((group) =>
            group.section ? (
              <div
                key={group.section.key}
                className="flex flex-col gap-16 rounded-[6px] bg-canvas-highlight-default p-16"
              >
                <div className="body-bold-400 text-text-primary">{group.section.title}</div>
                {group.items.map((item) => (
                  <ChecklistItemCard
                    key={item.key}
                    item={item}
                    readOnly
                    contained
                    programTypes={template.programTypes}
                  />
                ))}
              </div>
            ) : (
              <React.Fragment key="__ungrouped__">
                {group.items.map((item) => (
                  <ChecklistItemCard key={item.key} item={item} readOnly programTypes={template.programTypes} />
                ))}
              </React.Fragment>
            ),
          )}
        </div>
      </div>

      {/* Activity History Section */}
      <div className="flex flex-col gap-4">
        <h2 className="text-left heading-200 text-text-primary">Activity History</h2>
        {isHistoryFetching ? (
          <div>Loading...</div>
        ) : (
          <ActivityHistoryList>
            {pageVersions.map((version) => {
              const v = version as TemplateRow;
              return (
                <ActivityHistoryItem
                  key={v.id}
                  title={v.version === 1 ? `"${v.name}" created` : `New version saved as "${v.name}"`}
                  date={v.meta?.createdAt}
                  author={v.createdByEmail ? `${v.createdByName}, ${v.createdByEmail}` : v.createdByName}
                  note={v.description}
                />
              );
            })}
          </ActivityHistoryList>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <PalmettoButton emphasis="secondary" size="small" onClick={handlePreviousPage} disabled={isFirstPage}>
              Previous
            </PalmettoButton>
            <div className="text-sm">
              Page {page + 1} of {totalPages}
            </div>
            <PalmettoButton emphasis="secondary" size="small" onClick={handleNextPage} disabled={isLastPage}>
              Next
            </PalmettoButton>
          </div>
        )}
      </div>

      <PalmettoModal
        open={isPublishModalOpen}
        onClose={handleClosePublishModal}
        invertedBackground={false}
        bottomContent={
          <div className="flex justify-end gap-8">
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleClosePublishModal}>
              Cancel
            </PalmettoButton>
            <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handlePublishModalConfirm}>
              Publish
            </PalmettoButton>
          </div>
        }
      >
        <div className="flex flex-col gap-16">
          <h2>Publish this draft?</h2>
          <p className="text-text-secondary">This draft will replace the current active template once published.</p>
          <Controller
            name="draftNotes"
            control={publishModalControl}
            render={({ field }) => (
              <PalmettoTextarea
                id="publish-modal-draft-notes"
                label="Draft Notes"
                placeholder="Notes about this draft"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={3}
              />
            )}
          />
        </div>
      </PalmettoModal>
    </DsmPageContainer>
  );
};
